import { useSchoolYear } from '@pelckmans/business-components/context/SchoolYearContext';
import groupBy from 'lodash/groupBy';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentOrPreviousGroup, fetchCurrentAndPreviousGroups, fetchMembers, Member } from '../../../../api/groups';
import { Module } from '../../../../api/module';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import ModuleContext from '../../_contexts/ModuleContext';
import { fetchScorableItems, ScorableItem } from '../api/scorable-items';

type SchoolYear = ReturnType<typeof useSchoolYear>['schoolYears'][number];
export interface SchoolYearValue extends SchoolYear {
  id: string;
  title: string;
}

export type ResultModule = Module & { isVirtualExerciseModule?: boolean };

export interface ResultDataContextType {
  schoolYears:
    | {
        current: SchoolYearValue;
        previous: SchoolYearValue;
      }
    | undefined;
  groups: CurrentOrPreviousGroup[];
  members: Record<string, Member[]>;
  loading: {
    groups: boolean;
    members: boolean;
    scorableItems: boolean;
    schoolYears: boolean;
  };
  modules: ResultModule[];
  scorableItemsPerSchoolYearPerModule: Record<string, Record<string, ScorableItem[]>>;
  scorableItems: ScorableItem[];
}

export const ResultsDataContext = createContext<ResultDataContextType | undefined>(undefined);

function useGroups() {
  const [groups, setGroups] = useState<CurrentOrPreviousGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const handleError = useThrowAsyncError();

  useEffect(() => {
    let current = true;

    setIsLoading(true);

    fetchCurrentAndPreviousGroups()
      .then(fetchedGroups => {
        if (current) setGroups(fetchedGroups.filter(group => group.acceptedMemberCount > 0));
      })
      .catch(handleError)
      .finally(() => {
        if (current) setIsLoading(false);
      });

    return () => {
      current = false;
    };
  }, [handleError]);

  return {
    groups,
    isLoading,
  };
}

export interface UseMembersGroup {
  id: string;
  schoolYear: string;
}

export function useMembers(groups: UseMembersGroup[]) {
  const [members, setMembers] = useState<Record<string, Member[]>>({});
  const [isLoading, setLoading] = useState(false);
  const handleError = useThrowAsyncError();

  const fetchMembersForGroups = useCallback(
    async (groups: UseMembersGroup[]) => {
      const groupsToFetch = groups.filter(group => !members[group.id]);

      if (groupsToFetch.length === 0) return;

      setLoading(true);

      Promise.all(groupsToFetch.map(group => fetchMembers(group.schoolYear, group.id)))
        .then(membersPerGroup => {
          setMembers(prev => ({
            ...prev,
            ...Object.fromEntries(
              groupsToFetch.map((group, i) => [group.id, membersPerGroup[i].filter(x => x.status === 'accepted')]),
            ),
          }));
        })
        .catch(handleError)
        .finally(() => {
          setLoading(false);
        });
    },
    [handleError, members],
  );

  useEffect(() => {
    fetchMembersForGroups(groups);
  }, [fetchMembersForGroups, groups]);

  return {
    members,
    isLoading,
  };
}

function useSchoolYearValues() {
  const { currentSchoolYear, previousSchoolYear } = useSchoolYear();

  const { t } = useTranslation();

  const loading = useMemo(() => {
    return !previousSchoolYear || !currentSchoolYear;
  }, [previousSchoolYear, currentSchoolYear]);

  const schoolYears = useMemo(() => {
    return loading
      ? undefined
      : {
          previous: {
            ...previousSchoolYear!,
            id: `${previousSchoolYear!.from.getFullYear()}-${previousSchoolYear!.to.getFullYear()}`,
            title: t('PAGES.RESULTS.PER_STUDENT.FILTER.SCHOOL_YEAR.OPTIONS.PREVIOUS'),
          },
          current: {
            ...currentSchoolYear,
            id: `${currentSchoolYear.from.getFullYear()}-${currentSchoolYear.to.getFullYear()}`,
            title: t('PAGES.RESULTS.PER_STUDENT.FILTER.SCHOOL_YEAR.OPTIONS.CURRENT'),
          },
        };
  }, [previousSchoolYear, currentSchoolYear, t, loading]);

  return { loading, schoolYears };
}

function useModules() {
  const { module, subModules } = useContext(ModuleContext);
  const { t } = useTranslation();

  const modules: ResultModule[] = useMemo(() => {
    if (module) {
      const visibleModules: ResultModule[] = [module, ...subModules].filter(
        x => x.publishingState === 'published' || x.publishingState === 'phasedOut',
      );

      if (module.hasExerciseEntrance) {
        visibleModules.push({
          ...module,
          id: `${module.id}-vem`,
          catalogName: t('MODULES.VIRTUAL_EXERCISE_MODULE'),
          name: t('MODULES.VIRTUAL_EXERCISE_MODULE'),
          niceName: t('MODULES.VIRTUAL_EXERCISE_MODULE'),
          isVirtualExerciseModule: true,
        });
      }

      return visibleModules;
    }

    return [];
  }, [module, subModules, t]);

  return modules;
}

function useScorableItems() {
  const [fetchedScorableItems, setFetchedScorableItems] = useState<{ schoolYearId: string; data: ScorableItem[] }[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const { module } = useContext(ModuleContext);
  const handleError = useThrowAsyncError();
  const { schoolYears, loading: schoolYearsLoading } = useSchoolYearValues();

  const scorableItems = useMemo(() => {
    return fetchedScorableItems.flatMap(x => x.data);
  }, [fetchedScorableItems]);

  const scorableItemsPerSchoolYearPerModule = useMemo(() => {
    return fetchedScorableItems.reduce((acc, { data, schoolYearId }) => {
      acc[schoolYearId] = groupBy(data, 'module');
      acc[schoolYearId][`${module!.id}-vem`] = data;
      return acc;
    }, {} as Record<string, Record<string, ScorableItem[]>>);
  }, [fetchedScorableItems, module]);

  useEffect(() => {
    let current = true;

    setIsLoading(true);

    if (!module || schoolYearsLoading) return;

    Promise.all(
      Object.values(schoolYears!)
        .map(schoolYear =>
          fetchScorableItems(module!.id, schoolYear!.id, true).then(res => ({
            schoolYearId: schoolYear!.id,
            data: res,
          })),
        )
        .flat(),
    )
      .then(fetchedScorableItems => {
        if (current) {
          setFetchedScorableItems(
            fetchedScorableItems.map(item => {
              return {
                ...item,
                data: item.data.reduce((acc, item) => {
                  if (item.isAssignment) {
                    acc.push({
                      ...item,
                      id: `${item.id}-true`,
                      isFreeExercise: false,
                      isAssignment: true,
                    });
                  }
                  if (item.isFreeExercise) {
                    acc.push({
                      ...item,
                      id: `${item.id}-false`,
                      isFreeExercise: true,
                      isAssignment: false,
                    });
                  }

                  return acc;
                }, [] as ScorableItem[]),
              };
            }),
          );
        }
      })
      .catch(handleError)
      .finally(() => {
        if (current) setIsLoading(false);
      });

    return () => {
      current = false;
    };
  }, [module, schoolYears, handleError, schoolYearsLoading]);

  return {
    scorableItems,
    scorableItemsPerSchoolYearPerModule,
    isLoading,
  };
}

export function ResultsDataContextProvider({ children }: { children: React.ReactNode }) {
  const { groups, isLoading: groupsLoading } = useGroups();
  const { members, isLoading: membersLoading } = useMembers(groups);
  const { schoolYears, loading: schoolYearsLoading } = useSchoolYearValues();
  const modules = useModules();
  const { scorableItems, scorableItemsPerSchoolYearPerModule, isLoading: scorableItemsLoading } = useScorableItems();

  const loading = useMemo(() => {
    return {
      schoolYears: schoolYearsLoading,
      groups: groupsLoading,
      members: membersLoading,
      scorableItems: scorableItemsLoading,
    };
  }, [groupsLoading, membersLoading, scorableItemsLoading, schoolYearsLoading]);

  return (
    <ResultsDataContext.Provider
      value={{
        schoolYears,
        groups,
        members,
        loading,
        modules,
        scorableItems,
        scorableItemsPerSchoolYearPerModule,
      }}
    >
      {children}
    </ResultsDataContext.Provider>
  );
}

export function useResultsDataContext() {
  const context = useContext(ResultsDataContext);

  if (!context) {
    throw new Error('useResultsDataContext must be used within a ResultsDataContextProvider');
  }

  return context;
}
